import { SocialLogin } from '@capgo/capacitor-social-login';

/**
 * Native Sign in with Apple / Google bridge for the Capacitor iOS shell.
 *
 * The web build signs in by redirecting the whole page to the provider's OAuth
 * URL (see AuthContext.login). That flow is wrong inside the native WebView:
 * Google blocks its OAuth pages in embedded webviews, and Apple expects the
 * system sheet. So on native we drive the platform sheets directly through
 * @capgo/capacitor-social-login (native Google Sign-In SDK + Authentication
 * Services on iOS), obtain an identity/ID token, and POST it to the backend's
 * /native verification endpoints (see authService.native*SignIn).
 *
 * Gate every call on isNativeApp() — on the web the plugin has no native bridge
 * and login() would reject. The web redirect flow stays untouched.
 */

// The iOS OAuth client ID is the audience of the Google id_token and must match
// GOOGLE_OAUTH_IOS_CLIENT_ID on the server. It is non-secret (it ships in the
// app bundle regardless); baked at build time with a fallback to the same
// default the server uses so a missing env can't silently break sign-in.
const GOOGLE_IOS_CLIENT_ID =
  import.meta.env.VITE_GOOGLE_IOS_CLIENT_ID ||
  '598876562657-i9mfo0osud8bpbalstjl9njkqrrk40q3.apps.googleusercontent.com';

/** Thrown when the user dismisses the native provider sheet. Not a failure. */
export class SocialAuthCancelledError extends Error {
  constructor() {
    super('Sign-in was cancelled');
    this.name = 'SocialAuthCancelledError';
  }
}

export const isSocialAuthCancelled = (error: unknown): boolean =>
  error instanceof SocialAuthCancelledError;

export interface NativeAppleCredential {
  identityToken: string;
  // Apple only returns the name on the very first authorization, and only to
  // the client (never inside the token) — so it may be absent.
  givenName?: string;
  familyName?: string;
}

export interface NativeGoogleCredential {
  idToken: string;
}

// initialize() is idempotent from the caller's side: memoized so the first
// sign-in configures the providers and later ones reuse it. On failure the
// promise is cleared so a subsequent attempt can retry.
let initPromise: Promise<void> | null = null;

const ensureInitialized = (): Promise<void> => {
  if (!initPromise) {
    initPromise = SocialLogin.initialize({
      google: { iOSClientId: GOOGLE_IOS_CLIENT_ID },
      // Empty redirectUrl => use the native sheet with no web redirect on iOS.
      apple: { redirectUrl: '' },
    }).catch((error) => {
      initPromise = null;
      throw error;
    });
  }
  return initPromise;
};

// The plugin rejects with code 'USER_CANCELLED' on dismissal; surface that as a
// typed error and normalize everything else to a real Error. Native rejections
// arrive as plain objects (not Error instances), so pull the message out of the
// usual fields rather than String()-ing the object into "[object Object]".
const normalize = (error: unknown): never => {
  const err = error as { code?: string; message?: string; errorMessage?: string } | null;
  if (err?.code === 'USER_CANCELLED') throw new SocialAuthCancelledError();
  if (error instanceof Error) throw error;

  let message = err?.message || err?.errorMessage || (typeof error === 'string' ? error : '');
  if (!message) {
    try {
      message = JSON.stringify(error);
    } catch {
      message = String(error);
    }
  }
  const wrapped = new Error(message || 'Native sign-in failed');
  if (err?.code) (wrapped as Error & { code?: string }).code = err.code;
  throw wrapped;
};

export const signInWithApple = async (): Promise<NativeAppleCredential> => {
  await ensureInitialized();
  try {
    const { result } = await SocialLogin.login({
      provider: 'apple',
      options: { scopes: ['name', 'email'] },
    });
    if (!result.idToken) {
      throw new Error('Apple did not return an identity token');
    }
    return {
      identityToken: result.idToken,
      givenName: result.profile?.givenName ?? undefined,
      familyName: result.profile?.familyName ?? undefined,
    };
  } catch (error) {
    return normalize(error);
  }
};

export const signInWithGoogle = async (
  options?: { forcePrompt?: boolean },
): Promise<NativeGoogleCredential> => {
  await ensureInitialized();
  try {
    const { result } = await SocialLogin.login({
      provider: 'google',
      // forcePrompt shows Google's account chooser instead of silently
      // restoring the last-used account (the SDK default). We set it on the
      // sign-up path so users land on the right account; sign-in stays fast.
      options: { scopes: ['profile', 'email'], forcePrompt: options?.forcePrompt ?? false },
    });
    // Online mode carries the OIDC id_token (with email_verified); offline mode
    // returns only a serverAuthCode, which the backend endpoint can't consume.
    if (result.responseType !== 'online' || !result.idToken) {
      throw new Error('Google did not return an ID token');
    }
    return { idToken: result.idToken };
  } catch (error) {
    return normalize(error);
  }
};
